import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import API from "../api/api01";

interface PermissionContextType {
  allowedSlugs: Set<string> | null;
  loading: boolean;
  isAllowed: (slug: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: ReactNode; userRole: string | null }> = ({ children, userRole }) => {
  const [allowedSlugs, setAllowedSlugs] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async () => {
    if (!userRole) {
      setAllowedSlugs(new Set());
      setLoading(false);
      return;
    }

    if (userRole?.toLowerCase() === "superadmin") {
      setAllowedSlugs(new Set(["ALL"]));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data } = await API.get("/superadmin/sidebar-tree");
      const slugs = new Set<string>();
      
      const extractSlugs = (nodes: any[]) => {
        nodes.forEach((node) => {
          if (node.slug) slugs.add(node.slug);
          if (node.children) extractSlugs(node.children);
        });
      };
      
      extractSlugs(data);
      setAllowedSlugs(slugs);
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
      setAllowedSlugs(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [userRole]);

  const isAllowed = (slug: string) => {
    if (!allowedSlugs) return false;
    if (allowedSlugs.has("ALL")) return true;
    return allowedSlugs.has(slug);
  };

  return (
    <PermissionContext.Provider value={{ allowedSlugs, loading, isAllowed, refreshPermissions: fetchPermissions }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
};
